import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FocusModeViewProps {
  /** What to show in the slim header strip. */
  title: string;
  /** Whether the editor has unsaved changes — rendered as a badge by the caller. */
  dirty?: boolean;
  /**
   * Controls in the header, left of the close button — view/format
   * selectors. A slot, since which controls belong in focus mode is the
   * page's decision, not the overlay's.
   */
  headerControls?: ReactNode;
  /**
   * The save-state control rendered next to the title. Provided as a
   * slot (rather than a `dirty` boolean the overlay renders itself) so
   * the badge can double as a save affordance — see `SaveStatusBadge`,
   * which owns the distinct "draft vs. published" wording that must not
   * be collapsed into a bare "Saved".
   */
  statusBadge?: ReactNode;
  /** Footer strip (document statistics) rendered below the editor. */
  footer?: ReactNode;
  children: ReactNode;
  onClose: () => void;
}

/**
 * Full-viewport, distraction-free editor mode for the article editor.
 *
 * The article editor is one of the heaviest UI surfaces in the app —
 * toolbar, preview panes, metadata inputs, a big page around it — which
 * becomes noise once you actually want to *write*. Focus mode reduces
 * that to: a slim header strip (article title, optional controls,
 * exit/close) above the editor, filling the whole browser viewport.
 *
 * It deliberately does not attempt to also support the publication
 * editor: publications have section lists, block-level metadata, and
 * non-editor controls that a focus mode would need to orchestrate,
 * which is beyond the scope of "make this screen's editor fullscreen".
 */
export function FocusModeView({ title, headerControls, statusBadge, footer, children, onClose }: FocusModeViewProps) {
  // Lock background scroll so the overlay genuinely takes over the whole
  // browser window instead of fighting page-level scrolling beneath it.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* The header sits flush against the editor — no spacer gap — so
          the entire viewport is the writing surface. */}
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-medium">{title}</p>
          {statusBadge}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {headerControls}
          <Button type="button" variant="ghost" size="icon" className="size-8" onClick={onClose} aria-label="Exit focus mode">
            <X className="size-4" />
          </Button>
        </div>
      </header>

      <main className="flex min-h-0 flex-1 flex-col px-4 py-2 sm:px-6">
        {children}
      </main>

      {footer && (
        <footer className="flex shrink-0 items-center justify-end gap-4 border-t border-border px-4 py-1.5 text-xs text-muted-foreground">
          {footer}
        </footer>
      )}
    </div>
  );
}
